import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import { token } from '@bp-files/ui';
import { InkPreview } from '@/components/Signature/InkPreview';
import type { SignatureInk } from '@/components/Signature/signatureInk';
import { isTypingTarget } from '@/components/Viewer/keyboard';
import { COARSE_POINTER_QUERY } from '@/lib/breakpoints';
import { useMediaQuery } from '@/lib/useMediaQuery';
import {
  clampToPage,
  fieldAt,
  rectFromScreen,
  rectToScreen,
  MIN_FIELD_POINTS,
  type FieldRect,
  type PageView,
} from './signField';
import { SIGNATURE_CURSOR } from './signatureCursor';
import { SIGNING_COPY } from './signing.copy';
import type { SignField, SignFieldKind } from './signPdf';

/**
 * The fields placed on one page, and every way of moving them (FIL-8c).
 *
 * **An overlay, not part of the canvas.** The page underneath is re-rendered on
 * every zoom (FIL-8b), and a field drawn into that canvas would be redrawn with
 * it — at the cost of a re-render per drag frame, and with no way to give a box
 * a focus ring or a keyboard. So the fields are DOM, absolutely positioned over
 * the page, and their positions come from `signField.ts`'s tested arithmetic
 * rather than from anything measured here.
 *
 * **The rect a field carries is in PDF points against the unrotated page, and
 * nothing here stores a pixel.** Zoom, rotate and resize all change where a box
 * appears and none of them changes where the person put it — which is also what
 * lets the same rect be handed to `signPdf` without conversion.
 *
 * **Everything is pointer-events-driven and nothing is a library**, as
 * `SignaturePad` and `ZoomStage` already do it. Three rules govern all of it,
 * and all three were written by a phone.
 *
 * **A press is a candidate; a release is the act.** Placement used to fire on
 * `pointerdown`, which on a touchscreen meant that touching the document to
 * look at it dropped a signature on it — and that pinching to zoom, being two
 * presses, dropped two. So a press only *arms* a placement, and the release
 * performs it, provided the finger stayed within `TAP_SLOP_PX` and stayed
 * alone. The cost, which is the right way round but will be the complaint: iOS
 * Safari cancels a slow or rolling tap fairly eagerly, so a careful press can
 * do nothing and the second attempt has to be firmer. A lost tap beats a stray
 * stamp.
 *
 * **A stamp owns its touch; the page owns everything outside a stamp.** The
 * boxes and the resize handle are `touch-none`, so a finger on one drags it
 * instead of scrolling the column out from under it — which is what used to
 * happen, `pointercancel` and all, every time somebody reached for the corner.
 * The layer itself must never be `touch-none`: it covers the whole page and is
 * `pointer-events: auto` for as long as a tool is armed, so restricting it
 * would stop the document scrolling and pinching for the whole of sign mode.
 * That is the bug, not the fix.
 *
 * **A second pointer is never this file's.** Nothing here zooms. It used to be
 * the *browser's* — `index.html` leaves user scaling on and page zoom was the
 * pinch on a phone — and since ADR-0065 it is `PdfStage`'s, which listens in
 * the capture phase precisely because the stamps below stop propagation. The
 * swap was made because page zoom magnified the toolbars along with the
 * document. Nothing changes here either way: the moment a second pointer
 * appears, this layer's answer is to get out of the way, and every drag is
 * pinned to the one pointer id that started it, so a second finger can neither
 * hijack it nor end it.
 *
 * **The pinch this layer never sees ends a drag too** (ADR-0101). `PdfStage`
 * starts one from any two touch pointers on the *stage*, which this layer is
 * only part of — so a drag pinned to a finger here stayed live while the column
 * was being magnified, and every conversion below divides by `view.scale`,
 * which a running transform has briefly made untrue. `view.gesturing` is how
 * that arrives, and abandoning is the answer rather than compensating: the
 * reader with two fingers down is zooming, not placing.
 */

/** The noun a field reads as, for its group and its remove button. */
function kindNoun(kind: SignFieldKind): string {
  if (kind === 'signature') return SIGNING_COPY.field.signature;
  if (kind === 'draw') return SIGNING_COPY.field.drawing;
  return SIGNING_COPY.field.text;
}

/** How far a corner control extends, in CSS pixels. A mouse's target. */
const HANDLE_PX = 14;

/**
 * The same control for a finger. WCAG 2.5.8 asks 24×24 as a floor; 28 clears it
 * with room for the fact that half of the thing sits outside the box it grabs.
 */
const COARSE_HANDLE_PX = 28;

/** How far a finger may travel and still be a tap rather than a scroll, in CSS pixels. */
const TAP_SLOP_PX = 8;

interface Drag {
  id: string;
  /**
   * Which pointer owns it. Without this a second finger landing on the same
   * box overwrote the origin below, and the first finger's next move applied
   * its delta against the *other* finger's start — so the stamp jumped by the
   * distance between the two fingers, mid-pinch.
   */
  pointerId: number;
  mode: 'move' | 'resize';
  /** Where the pointer started, in CSS pixels within the page. */
  fromX: number;
  fromY: number;
  /** The field's box when the drag began, in CSS pixels. */
  start: FieldRect;
}

/**
 * A press that has not yet become a placement.
 *
 * Held in **client** coordinates rather than layer ones: the layer moves when
 * the column scrolls, so a layer-relative delta would measure the document's
 * travel as well as the finger's and reject a tap that never moved.
 */
interface PendingTap {
  pointerId: number;
  clientX: number;
  clientY: number;
}

export interface FieldLayerProps {
  pageIndex: number;
  view: PageView;
  fields: SignField[];
  /** The armed tool, or `undefined` when sign mode is on but no tool is chosen. */
  tool: SignFieldKind | undefined;
  ink: SignatureInk | undefined;
  onPlace: (field: SignField) => void;
  onChange: (id: string, rect: FieldRect) => void;
  onText: (id: string, text: string) => void;
  onRemove: (id: string) => void;
  /**
   * Closes a continuous edit for undo — the end of a drag or a resize
   * (`pointerup`), or leaving a text field (`blur`). `onChange`/`onText` fire
   * many times through one gesture; this marks where one ends so it undoes as a
   * single step rather than frame by frame.
   */
  onEndEdit: () => void;
}

export function FieldLayer({
  pageIndex,
  view,
  fields,
  tool,
  ink,
  onPlace,
  onChange,
  onText,
  onRemove,
  onEndEdit,
}: FieldLayerProps) {
  const layerRef = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | undefined>(undefined);
  /*
    Pointers currently down on the layer's own background. Two of them is a
    pinch. A `Set` rather than `ZoomStage`'s `Map<number, Point>`: that file
    needs both positions to compute a spread, and this one needs only the count.
  */
  const down = useRef(new Set<number>());
  const pending = useRef<PendingTap | undefined>(undefined);
  const [selected, setSelected] = useState<string | undefined>(undefined);

  /**
   * A pinch anywhere on the stage ends whatever this layer was doing.
   *
   * **The header's rule, widened to a pointer this layer never saw.** A drag is
   * pinned to the one finger that started it, and a second finger landing on
   * the layer's own background is already refused — but `PdfStage` starts a
   * pinch from any two touch pointers on the *stage*, which the layer is only
   * part of. The drag then stayed live while the column was being magnified,
   * and every screen→points conversion here divides by `view.scale`, which a
   * running transform has made briefly untrue: the field tracked the finger at
   * the wrong rate and was stored where it was never dropped.
   *
   * Abandoning is the right answer rather than compensating. The reader who put
   * two fingers down is zooming, not placing, and a field left where it was is
   * a gesture that did nothing — which is what they asked for.
   */
  useEffect(() => {
    if (view.gesturing !== true) return;

    drag.current = undefined;
    pending.current = undefined;
  }, [view.gesturing]);

  /** Fingers, not a mouse. A 1280px tablet still has thumbs. */
  const coarse = useMediaQuery(COARSE_POINTER_QUERY);
  const handlePx = coarse ? COARSE_HANDLE_PX : HANDLE_PX;

  /*
    The floor for a resize, in the pixels the drag is measured in.

    **`clampToPage` is what actually enforces it**, in points; this is the same
    number said in the drag's own units so the box stops where the finger does
    instead of snapping back a frame later. It used to be `HANDLE_PX`, which is
    a size in a different space entirely — and so the smallest a field could be
    was a function of the zoom: 47pt while zoomed out to a third, 7pt at 2×,
    where the clamp bounced it back and the box stuck.
  */
  const minBoxPx = MIN_FIELD_POINTS * view.scale;

  const mine = fields.filter((field) => field.pageIndex === pageIndex);

  function pointIn(event: { clientX: number; clientY: number }): { x: number; y: number } {
    const rect = layerRef.current?.getBoundingClientRect();
    if (rect === undefined) return { x: 0, y: 0 };

    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  /** Ends a drag only for the pointer that started it. */
  function endDrag(event: ReactPointerEvent<Element>): void {
    event.stopPropagation();
    if (drag.current?.pointerId !== event.pointerId) return;

    drag.current = undefined;
    release(event.currentTarget, event.pointerId);
    // The drag or resize is over — close its one undo step.
    onEndEdit();
  }

  return (
    <div
      ref={layerRef}
      data-testid={`field-layer-${String(pageIndex)}`}
      className="absolute inset-0"
      /*
        Transparent to the pointer unless a tool is armed, so reading a document
        in sign mode still selects its text and scrolls normally. The fields
        themselves opt back in individually.
      */
      style={{
        pointerEvents: tool === undefined ? 'none' : 'auto',
        // Only the signature tool gets a stamp-shaped cursor — a text field
        // is placed the same way but is not "a signature about to land".
        cursor: tool === 'signature' ? SIGNATURE_CURSOR : undefined,
      }}
      onPointerDown={(event) => {
        if (tool === undefined) return;
        // Only a press on the page itself can become a placement; one that
        // started on an existing box is a drag, and that box stopped it
        // reaching here.
        if (event.target !== event.currentTarget) return;

        /*
          A primary pointer is by definition the only one of its type down, so
          its arrival is also the moment to forget anything the registry
          leaked. **This looks like a defensive no-op and is the most important
          line in the gate.** A `pointerup` that never came back — a tab that
          lost focus mid-gesture, a finger the browser stopped reporting —
          would otherwise leave an id here forever, making every later tap read
          as the second half of a pinch: placement would stop working for the
          life of the document, silently, with nothing on screen to explain it.
        */
        if (event.isPrimary) down.current.clear();
        down.current.add(event.pointerId);

        if (down.current.size > 1) {
          // A second finger. Whatever the first one was about to do, it is now
          // half of a pinch, and a pinch is the browser's.
          pending.current = undefined;
          return;
        }

        capture(event.currentTarget, event.pointerId);
        pending.current = {
          pointerId: event.pointerId,
          clientX: event.clientX,
          clientY: event.clientY,
        };
      }}
      onPointerMove={(event) => {
        const tap = pending.current;
        if (tap?.pointerId !== event.pointerId) return;

        // Travelled too far to be a tap. The capture above is what makes this
        // measurement honest: without it the moves stop arriving the moment the
        // finger leaves the page's box, and a long drag that came back and
        // lifted would still place.
        if (Math.hypot(event.clientX - tap.clientX, event.clientY - tap.clientY) > TAP_SLOP_PX) {
          pending.current = undefined;
        }
      }}
      onPointerUp={(event) => {
        down.current.delete(event.pointerId);

        const tap = pending.current;
        // Not this pointer's release: a second finger lifting, or a release
        // for a press that was already disqualified. Either way, nothing to do.
        if (tap?.pointerId !== event.pointerId) return;

        pending.current = undefined;
        release(event.currentTarget, event.pointerId);

        if (tool === undefined) return;
        /*
          Checked again, and not redundantly. In a browser the capture above
          retargets this release to the layer, so the guard passes; in jsdom
          there is no pointer capture at all, so a release that bubbled up from
          a field box arrives still pointing at the box, and this is what turns
          it away.
        */
        if (event.target !== event.currentTarget) return;

        const point = pointIn(event);
        const rect = fieldAt(point, view, tool === 'signature' ? (ink?.aspect ?? 3) : 4, 36);

        onPlace({
          // `crypto.randomUUID` is available in every browser this product
          // supports and in jsdom; the id is local and never leaves the tab.
          id: crypto.randomUUID(),
          kind: tool,
          pageIndex,
          rect,
          ...(tool === 'text' ? { text: '' } : {}),
        });
      }}
      onPointerCancel={(event) => {
        // The scroll container claimed the gesture, or the browser started its
        // own pinch-zoom. This was never a tap.
        down.current.delete(event.pointerId);
        if (pending.current?.pointerId !== event.pointerId) return;

        pending.current = undefined;
        release(event.currentTarget, event.pointerId);
      }}
    >
      {mine.map((field) => {
        const box = rectToScreen(field.rect, view);
        const isSelected = selected === field.id;
        // A drawing is delete-only: it stays exactly where and how it was drawn,
        // so it carries no move drag and no resize handle.
        const isDraw = field.kind === 'draw';

        return (
          <div
            key={field.id}
            data-testid={`field-${field.id}`}
            role="group"
            aria-label={SIGNING_COPY.field.label(kindNoun(field.kind), pageIndex + 1)}
            tabIndex={0}
            /*
              `touch-none`: a finger on a stamp moves the stamp. Without it the
              scroll container takes the gesture and cancels the drag, which is
              what made a placed signature impossible to nudge on a phone. The
              trade is stated in the module header — you scroll by touching the
              page, not by touching a stamp.
            */
            className="absolute touch-none rounded-sm border-2"
            style={{
              left: box.x,
              top: box.y,
              width: box.width,
              height: box.height,
              pointerEvents: 'auto',
              borderColor: isSelected ? token('bp-blue') : token('bp-border-strong'),
              // White, not `bp-paper`: the stamp sits directly on a rendered
              // PDF page, which is true white, and `bp-paper`'s off-white read
              // as a grey patch against it. `bp-paper` stays put on
              // `SignaturePad` and `StoredSignature` — see `Signature`'s
              // README section for why that pairing is pinned to the pad.
              background: token('bp-on-accent'),
              cursor: isDraw ? 'default' : 'move',
            }}
            onFocus={() => {
              setSelected(field.id);
            }}
            onKeyDown={(event) => {
              // Delete removes the focused field. No confirmation: nothing has
              // been uploaded, and placing it again is one click.
              if (event.key === 'Delete' || event.key === 'Backspace') {
                /*
                  Whose keystroke it is, decided by where it landed rather than
                  by what the field happens to contain. The text input is a
                  child of this box, so its Backspaces bubble here — and the
                  guard used to be "the text is not empty", which meant clearing
                  a field and pressing Backspace once more deleted the field out
                  from under somebody who was still typing in it.
                */
                if (isTypingTarget(event.target)) return;
                event.preventDefault();
                event.stopPropagation();
                onRemove(field.id);
              }
            }}
            onPointerDown={(event) => {
              if ((event.target as HTMLElement).dataset['handle'] === 'resize') return;
              event.stopPropagation();
              // A drawing does not move — selecting it (for keyboard Delete) is
              // all a press does. It has no drag and no resize handle.
              if (isDraw) {
                setSelected(field.id);
                return;
              }
              // One drag at a time, and it belongs to the finger that started
              // it. A second one arriving is a pinch, not a second drag.
              if (drag.current !== undefined) return;

              capture(event.currentTarget, event.pointerId);
              setSelected(field.id);

              const point = pointIn(event);
              drag.current = {
                id: field.id,
                pointerId: event.pointerId,
                mode: 'move',
                fromX: point.x,
                fromY: point.y,
                start: box,
              };
            }}
            onPointerMove={(event) => {
              const active = drag.current;
              if (active?.id !== field.id || active.pointerId !== event.pointerId) return;

              const point = pointIn(event);
              const dx = point.x - active.fromX;
              const dy = point.y - active.fromY;

              const moved: FieldRect =
                active.mode === 'move'
                  ? { ...active.start, x: active.start.x + dx, y: active.start.y + dy }
                  : {
                      ...active.start,
                      width: Math.max(minBoxPx, active.start.width + dx),
                      height: Math.max(minBoxPx, active.start.height + dy),
                    };

              onChange(field.id, clampToPage(rectFromScreen(moved, view), view.page));
            }}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            {field.kind === 'text' ? (
              <input
                aria-label={SIGNING_COPY.field.text}
                placeholder={SIGNING_COPY.field.textPlaceholder}
                value={field.text ?? ''}
                onChange={(event) => {
                  onText(field.id, event.target.value);
                }}
                // Leaving the field ends this run of typing, so the next one is
                // its own undo step rather than folding into this.
                onBlur={onEndEdit}
                // A drag that started in the input is text selection, not a move.
                onPointerDown={(event) => {
                  event.stopPropagation();
                }}
                // `touch-auto`, undoing the box's `touch-none` for this child
                // alone: the same reason the handler above lets the press
                // through. Caret placement and drag-select inside a text field
                // are the browser's, and on iOS they fight a restricted
                // ancestor.
                className="h-full w-full touch-auto bg-transparent px-1 outline-none"
                style={{ color: token('bp-ink'), fontSize: Math.max(8, box.height * 0.6) }}
              />
            ) : (
              // A signature draws the shared account mark; a drawing draws its
              // own ink, made on this document.
              <InkPreview
                ink={field.kind === 'draw' ? field.ink : ink}
                width={box.width}
                height={box.height}
              />
            )}

            {/*
              **Top-left, where it used to be top-right.** Both corner controls
              were on the trailing edge, one above the other, and they overlap
              whenever the box is shorter than they are — which a default 36pt
              signature is at any zoom below 100%, and every signature is once
              it has been shrunk. On opposite corners they only meet when the
              box is narrower than one control, and the handle below paints over
              this one in that case: an accidental resize is undone by dragging
              back, an accidental delete throws the placement away.
            */}
            <button
              type="button"
              aria-label={SIGNING_COPY.field.remove(kindNoun(field.kind))}
              onClick={(event) => {
                event.stopPropagation();
                onRemove(field.id);
              }}
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
              className="absolute flex touch-none items-center justify-center rounded-full text-xs leading-none"
              style={{
                left: -handlePx / 2,
                top: -handlePx / 2,
                width: handlePx,
                height: handlePx,
                background: token('bp-danger'),
                color: token('bp-paper'),
              }}
            >
              <RemoveIcon />
            </button>

            {/*
              One corner handle rather than eight. Eight is what a design tool
              needs; a signature box is dragged to roughly the right size once,
              and each extra handle is another thing between a keyboard user and
              the next field.

              **Last in the box, so it paints over the delete button** on a
              field too small to hold both — see that button's comment. And
              `touch-none`, for the box's reason: reaching for this corner with
              a finger used to scroll the document instead, every time.

              **A drawing has none of this.** It is delete-only — placed where it
              was drawn and never resized — so the handle is withheld for it.
            */}
            {isDraw ? null : (
              <span
                data-handle="resize"
                aria-hidden="true"
                className="absolute touch-none rounded-sm"
                style={{
                  right: -handlePx / 2,
                  bottom: -handlePx / 2,
                  width: handlePx,
                  height: handlePx,
                  background: token('bp-blue'),
                  cursor: 'nwse-resize',
                }}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  if (drag.current !== undefined) return;

                  capture(event.currentTarget, event.pointerId);
                  setSelected(field.id);

                  const point = pointIn(event);
                  drag.current = {
                    id: field.id,
                    pointerId: event.pointerId,
                    mode: 'resize',
                    fromX: point.x,
                    fromY: point.y,
                    start: box,
                  };
                }}
                onPointerMove={(event) => {
                  const active = drag.current;
                  if (active?.id !== field.id || active.mode !== 'resize') return;
                  if (active.pointerId !== event.pointerId) return;

                  const point = pointIn(event);
                  const resized: FieldRect = {
                    ...active.start,
                    width: Math.max(minBoxPx, active.start.width + (point.x - active.fromX)),
                    height: Math.max(minBoxPx, active.start.height + (point.y - active.fromY)),
                  };

                  onChange(field.id, clampToPage(rectFromScreen(resized, view), view.page));
                }}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** jsdom implements no pointer capture, and it can throw in a browser too. */
function capture(element: Element, pointerId: number): void {
  try {
    element.setPointerCapture(pointerId);
  } catch {
    // A drag without capture ends at the element's edge, which is survivable.
  }
}

/**
 * The other half, and not cosmetic.
 *
 * Implicit release on `pointerup` happens only for pointers that stop being
 * active — true of touch and pen, **false of a mouse**, which stays active
 * after the button comes up. So a mouse capture outlives its drag: the element
 * keeps swallowing every `pointermove` in the document, which suppresses hover
 * on everything behind it and starves the placement gate above of the moves it
 * uses to tell a drag from a tap.
 */
function release(element: Element, pointerId: number): void {
  try {
    element.releasePointerCapture(pointerId);
  } catch {
    // Nothing was captured, or this is jsdom. Either way there is nothing to undo.
  }
}

function RemoveIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={10}
      height={10}
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}
